import { DeckStage } from '../deck/stage';
import { WorkspaceMarkdown } from '../markdown';
import { tableChart, WorkspaceChart, WorkspaceTable } from '../table';
import type { ChartPayload, CompanyResult, DeckPayload, TablePayload } from '../types';

// A Result's content, read-only, as its Overview shows it. The Result canvas and the
// Result picture (assets/result-preview.html) draw it with the same components, so a
// picture is never a second layout of the work.

export interface ResultViews {
  markdown: string | null;
  deck: DeckPayload | null;
  chart: ChartPayload | null;
  table: TablePayload | null;
}

/** What a Result's retained payload can be shown as: a table is also its chart when it has one. */
export function resultViews(
  kind: CompanyResult['kind'],
  payload: CompanyResult['payload'] | null | undefined,
): ResultViews {
  const markdown =
    kind === 'markdown' ? ((payload as { markdown: string } | undefined)?.markdown ?? null) : null;
  const deck = kind === 'deck' ? ((payload as DeckPayload | undefined) ?? null) : null;
  const chart =
    kind === 'chart'
      ? ((payload as ChartPayload | undefined) ?? null)
      : kind === 'table' && payload
        ? tableChart(payload as TablePayload)
        : null;
  const table: TablePayload | null =
    kind === 'table'
      ? ((payload as TablePayload | undefined) ?? null)
      : chart
        ? {
            columns: [chart.xKey, ...chart.yKeys].map((key) => ({ key, label: key })),
            rows: chart.rows,
          }
        : null;
  return { markdown, deck, chart, table };
}

/** True when the payload has something the preview can draw: words, a first slide, a chart or a table. */
export const hasResultPreview = (views: ResultViews) =>
  Boolean(views.markdown?.trim()) ||
  (typeof views.deck?.slides?.[0] === 'object' && views.deck.slides[0] !== null) ||
  Boolean(views.chart) ||
  Boolean(views.table);

/** The Overview's content: the document, the chart (or the table without one), or a deck's first slide. */
export function ResultPreview({
  kind,
  payload,
}: {
  kind: CompanyResult['kind'];
  payload: CompanyResult['payload'] | null | undefined;
}) {
  const { markdown, deck, chart, table } = resultViews(kind, payload);
  if (deck) return deck.slides[0] ? <DeckStage slide={deck.slides[0]} /> : null;
  if (markdown !== null) return <WorkspaceMarkdown text={markdown} />;
  if (chart) return <WorkspaceChart chart={chart} columns={table?.columns} />;
  if (table) return <WorkspaceTable table={table} />;
  return null;
}
